import { http } from '@pendo/http';
import { BuildingBlockLayouts } from '@pendo/services/BuildingBlocks';
import { getGuideStepsContent } from '@/guides/helpers/common.util';
import flatten from 'lodash/flatten';
import { isWebBasedApp } from '@/utils/apps';
import { environmentBucket } from '@/settings/environment.utils';
import { VISITOR_ASSIST_INTEGRATION_PROVIDER } from '@/ai-agents/components/ai-agents.utils';

export const states = ['draft', 'staged', 'public'];

export const BUILT_IN_INTEGRATION_PROVIDERS = [VISITOR_ASSIST_INTEGRATION_PROVIDER];

export function isManagedIntegrationModule (resourceCenterConfig) {
    return (resourceCenterConfig?.moduleId === BuildingBlockLayouts.moduleIds.integration
        && !BUILT_IN_INTEGRATION_PROVIDERS.includes(resourceCenterConfig?.integrationProvider)
    );
}

export async function buildResourceCenterBadge () {
    // eslint-disable-next-line internal-rules/no-hardcoded-colors
    const color = '#FF4876';
    const contentHost = `https://${environmentBucket(true)}/badges`;

    const { data } = await http.get(`${contentHost}/pendo-chevron.svg`, { withCredentials: false });

    const badgeUrl = `data:image/svg+xml;base64,${window.btoa(data.replace(/#010101/g, color))}`;

    return badgeUrl;
}

/**
 * GETs a list of all the resource centers created for a specific app
 * @param {Number} appId
 */
export const getResourceCenterListByAppId = (appId) => {
    const endpoint = appId ? `/api/s/_SID_/resourcecenter?appId=${appId}` : '/api/s/_SID_/resourcecenter';

    return http.get(endpoint);
};

/**
 * GETs step content for every guide (including modules and homeView) contained in a specific resource
 * center state, either draft, staged, or public
 * @param {Object} resourceCenterState A specific resource center state (draft, staged, public)
 */
export const getResourceCenterGuideStepContentByState = (resourceCenterState) => {
    const { homeView } = resourceCenterState;

    return Promise.all([
        getGuideStepsContent(homeView.steps).then((stepContent) => {
            homeView.steps = stepContent;

            return homeView;
        }),
        Promise.all(
            resourceCenterState.modules.map((module) => {
                return getGuideStepsContent(module.steps).then((stepContent) => {
                    module.steps = stepContent;

                    return module;
                });
            }),
        ),
    ]);
};

/**
 * GETs step content for every guide (including modules and homeView) contained in a resource
 * center across all states (draft, staged, public)
 * @param {Object} resourceCenter
 */
export const getResourceCenterGuideStepContentForAllStates = (resourceCenter) => {
    return Promise.all(
        states.map((state) => {
            if (!resourceCenter[state]) {
                return Promise.resolve();
            }

            return getResourceCenterGuideStepContentByState(resourceCenter[state]);
        }),
    );
};

/**
 * Fetches all resources centers across all web application in a subscription, including
 * the step content for each guide (including modules and homeView) contained in the
 * resource center
 */
export const fetchResourceCenters = (integratedApps) => {
    const webAppIds = integratedApps.filter((app) => isWebBasedApp(app.platform)).map((app) => app.id);

    return Promise.all(
        webAppIds.map((appId) => {
            return getResourceCenterListByAppId(appId).then(({ data }) => data);
        }),
    ).then((resourceCenters) => {
        resourceCenters = flatten(resourceCenters);

        return Promise.all(resourceCenters.map(getResourceCenterGuideStepContentForAllStates)).then(() => {
            return { data: resourceCenters };
        });
    });
};

export const fetchResourceCenter = (homeViewId) => {
    let resourceCenter;

    return http
        .get(`/api/s/_SID_/resourcecenter/${homeViewId}`)
        .then(({ data }) => {
            resourceCenter = data;

            return getResourceCenterGuideStepContentForAllStates(resourceCenter);
        })
        .then(() => resourceCenter);
};

/**
 * Used to turn RC production state on/off
 * NOTE: endpoint currently takes no state, but sets isDisabled on public version
 * @param {string} homeViewId ID of the homeView for a given resourceCenter
 * @param {boolean} isDisabled represents the desired state of the resourceCenter
 */
export const disableProductionResourceCenter = (homeViewId, isDisabled) => {
    let resourceCenter;
    const endpoint = `/api/s/_SID_/resourcecenter/${homeViewId}/setdisabled`;
    const payload = { isDisabled };

    return http
        .post(endpoint, payload)
        .then(({ data }) => {
            resourceCenter = data;

            return getResourceCenterGuideStepContentForAllStates(resourceCenter);
        })
        .then(() => resourceCenter);
};

/**
 * Used to promote a Resource Center from on state to another
 * @param {string} homeViewId ID of the homeView for the target resourceCenter
 * @param {string} toState ('staged' | 'public') target state for resourceCenter promotion
 */
export const promoteResourceCenter = (homeViewId, fromState, toState) => {
    let resourceCenter;
    const endpoint = `api/s/_SID_/resourcecenter/${homeViewId}/promote/${toState}?fromState=${fromState}`;

    return http
        .post(endpoint, { toState })
        .then(({ data }) => {
            resourceCenter = data;

            return getResourceCenterGuideStepContentForAllStates(resourceCenter);
        })
        .then(() => resourceCenter);
};

export async function createStepId () {
    const response = await http.get('/api/s/_SID_/object/makeid');

    return response.data.id;
}

export async function createModuleId (homeViewId) {
    const response = await http.get(`/api/s/_SID_/resourcecenter/${homeViewId}/module/makeid`);

    return response.data.id;
}

/**
 * Create **or update** a module in a resource center
 *
 * @param {String} homeViewId The ID of the resource center's top level guide
 * @param {Object} module The module to be created or updated
 * @returns Whatever the API returns (which should be the entire updated RC)
 */
export async function createResourceCenterModule (homeViewId, module) {
    const moduleId = module.id;
    const response = await http.put(`/api/s/_SID_/resourcecenter/${homeViewId}/module/${moduleId}`, module);

    return response.data;
}

export async function addModuleToResourceCenter (homeViewId, module) {
    const response = await http.post(`/api/s/_SID_/resourcecenter/${homeViewId}/module`, module);

    return response.data;
}

export async function deleteModuleFromResourceCenter (homeViewId, moduleId) {
    const response = await http.delete(`/api/s/_SID_/resourcecenter/${homeViewId}/module/${moduleId}`);

    return response.data;
}

/**
 * Used to update the rc name across resource centers versions
 * @param {string} homeViewId ID of the homeView for the target resourceCenter
 * @param {string} name new name for the target resourceCenter
 */
export const updateResourceCenterName = (homeViewId, name) => {
    const endpoint = `/api/s/_SID_/resourcecenter/${homeViewId}/name`;
    const payload = { name };

    let resourceCenter;

    return http
        .put(endpoint, payload)
        .then(({ data }) => {
            resourceCenter = data;

            return getResourceCenterGuideStepContentForAllStates(resourceCenter);
        })
        .then(() => resourceCenter);
};

/**
 * Used to update a resource center's authored language across resource centers versions
 * @param {string} homeViewId ID of the homeView for the target resourceCenter
 * @param {string} authoredLanguage the new authoredLanguage the target resourceCenter
 */
export const updateResourceCenterAuthoredLanguage = async (homeViewId, authoredLanguage) => {
    const endpoint = `/api/s/_SID_/resourcecenter/${homeViewId}/authoredlanguage`;
    const payload = { authoredLanguage };

    let resourceCenter;

    try {
        const { data } = await http.put(endpoint, payload);
        resourceCenter = data;
        await getResourceCenterGuideStepContentByState(resourceCenter.draft);
    } catch (error) {
        throw error;
    }

    return resourceCenter;
};

export const updateModuleSegment = (homeViewId, moduleId, audience, audienceUiHint) => {
    const endpoint = `api/s/_SID_/resourcecenter/${homeViewId}/module/${moduleId}/segment`;
    const payload = { audience, audienceUiHint };
    let resourceCenter;

    return http
        .put(endpoint, payload)
        .then(({ data }) => {
            resourceCenter = data;

            return getResourceCenterGuideStepContentForAllStates(resourceCenter);
        })
        .then(() => resourceCenter);
};

export const saveModuleGuideList = (homeViewId, moduleId, guideList) => {
    const endpoint = `api/s/_SID_/resourcecenter/${homeViewId}/module/${moduleId}/guides`;

    return http.put(endpoint, guideList).then(({ data }) => data);
};

export const deleteResourceCenterVersions = (homeViewId) => {
    const endpoint = `api/s/_SID_/resourcecenter/${homeViewId}`;

    return http.delete(endpoint);
};

export const updateModuleName = (homeViewId, moduleId, name) => {
    const endpoint = `api/s/_SID_/resourcecenter/${homeViewId}/module/${moduleId}/name`;
    const payload = { name };
    let resourceCenter;

    return http
        .put(endpoint, payload)
        .then(({ data }) => {
            resourceCenter = data;

            return getResourceCenterGuideStepContentForAllStates(resourceCenter);
        })
        .then(() => resourceCenter);
};

export const goToIntegrationsPage = (integrationProvider, integrationName, returnPath, router) => {
    router.push({
        name: 'IntegrationsSetup',
        query: {
            isFromResourceCenter: true,
            returnTo: returnPath,
        },
        params: { integrationProvider, integrationName },
    });
};
